import { eventHasRun, getEventHasRunValue } from '../../utils/eventTracker';
import getSubmissionTimestamp from '../../utils/getSubmissionTimestamp';
import getInvalidFields from '../../utils/getInvalidFields';
import getInvalidFieldsHTML5 from '../../utils/getInvalidFieldsHTML5';
import getPIIExistence from '../../utils/getPIIExistence';
import createOrUpdateHiddenFields from '../../utils/createOrUpdateHiddenFields';
import injectCAIWalletConfig from '../../utils/injectCAIWalletConfig';
import injectExtraInfoParams from '../../utils/injectExtraInfoParams';
import hasMyWalletInteraction from '../../utils/hasMyWalletInteraction';
import confirmationEvent from './confirmation';
import getFormEventData from '../../utils/getFormEventData';
import hashPii from '../../utils/hashPii';
import getSection from '../../utils/getSection';
import injectFormTrackingAttributes from '../../utils/injectFormTrackingAttributes';
import injectClickPath from '../../utils/injectClickPath';
import setFinanceFormSsnFieldLogging from '../../utils/setFinanceFormSsnFieldLogging';
import injectMyCars from '../../utils/injectMyCars';
import injectShiftSessionId from '../../utils/injectShiftSessionId';
import injectUniteCorrelationId from '../../utils/injectUniteCorrelationId';

export const DDC_FORM_TYPEDSEARCH = 'ddc.form.typedSearch';
export const DDC_FORM_SUBMISSION = 'ddc.form.submission';

async function submission(win, event = {}) {
	const {
		target: element,
		target: { tagName = '', textContent = '' } = {},
		timeStamp = 0
	} = event;

	const {
		DDC,
		DDC: {
			dataLayer,
			dataLayer: {
				page: {
					pageInfo: { isVdp, isVlp, pageName }
				},
				vehicles
			},
			WidgetData: {
				'ws-tracking': { paramsForExtraInfo }
			}
		}
	} = win;

	const eventObjects = [];
	const form = element?.closest('form');
	const $form = $(form);
	const fromTrackingType = form?.dataset?.formTrackingType;
	const eventType =
		fromTrackingType === 'typedSearch'
			? DDC_FORM_TYPEDSEARCH
			: DDC_FORM_SUBMISSION;

	injectClickPath({
		form,
		maxClickPathCount: parseInt(
			window?.DDC?.WidgetData?.['ws-tracking']?.maxClickpathCount || 100,
			10
		)
	});
	injectFormTrackingAttributes(form);
	setFinanceFormSsnFieldLogging(form);

	if (
		!eventHasRun(eventType, {
			timeStamp
		})
	) {
		const { confirmation } = dataLayer;
		const eventData = {
			formEventId:
				form?.querySelector('input[name="formEventId"]')?.value || '',
			// we pass submissionTimestamp if it exists in local storage
			// allows for a submission timestamp to come from another place
			//  such as: /cms-sites/htdocs/sites/a/autonation/js/price.js
			submissionTimestamp:
				localStorage?.getItem('submissionTimestamp') ||
				getSubmissionTimestamp(),
			element,
			...getPIIExistence(form, confirmation),
			...getFormEventData({
				form,
				vehicles,
				dataLayer
			})
		};

		const section = getSection(element, win.DDC) || '';
		const validator = $form.data('validator');
		const eventMapId = eventData.formEventId || eventData.trackingId;
		const formInitiationTime =
			getEventHasRunValue(eventMapId)?.formInitiationTime;
		eventData.elapsedTime = formInitiationTime
			? eventData.currentTime - formInitiationTime
			: 0;

		confirmation.element = element;

		// data passed to the confirmation page for the form confirmation event
		const fieldsToUpdateOrCreate = {
			'dl.custom.form.id': eventData.customFormId,
			'dl.element': `${tagName} ${textContent}`
				?.replace?.(/[\r\n ]+/gm, ' ')
				?.trim?.(),
			'dl.formElapsedTime': eventData.elapsedTime,
			'dl.formTrackingId': eventData.trackingId,
			'dl.fromVdp': isVdp,
			'dl.fromVlp': isVlp,
			'dl.isModal': eventData.isModal,
			'dl.params': $form.attr('action')?.split('?')[1] || '',
			'dl.widgetName': eventData.formTitle,
			'dl.pageName': pageName
		};

		eventData.formVehicleModel =
			form?.querySelector('[name="model"]')?.value || '';

		const uuid = isVdp && vehicles?.length > 0 && vehicles[0]?.uuid;
		if (uuid) {
			fieldsToUpdateOrCreate['dl.uuid'] = uuid;
		}

		// If form is typed search push a typedSearch event. If not, check that validation is enabled.
		// If enabled eventData.validate will be true when validation passes and false when validation fails.
		// If validation passes, push a form submission event including location and contact info.
		// If validation is not enabled, push a form submission event anyway showing validation is not enabled.

		if (fromTrackingType === 'typedSearch') {
			eventData.typedSearchContent = form?.querySelector(
				"input[name='search']"
			)?.value;
		} else if (validator) {
			eventData.zip =
				form?.querySelector(
					"input[data-id='contact.address.postalCode']"
				)?.value || '';
			eventData.preferredContact =
				form?.querySelector(
					"select[data-id='contact.preferredContact']"
				)?.value || '';

			// on the mobile send to phone form there is a jquery innerText error
			// that we catch here
			// it only happens when the form does not validate
			// TODO: Look into why that error happens
			try {
				eventData.validate = validator.checkValidity();
			} catch (e) {
				eventData.validate = false;
			}

			fieldsToUpdateOrCreate['dl.contact.address.postalCode'] =
				eventData.zip;
			fieldsToUpdateOrCreate['dl.contact.preferredContact'] =
				eventData.preferredContact;
			eventData.invalidFields = getInvalidFields(validator.getInputs());
		} else if (typeof validator === 'undefined') {
			eventData.validate = form?.checkValidity();
			eventData.invalidFields = getInvalidFieldsHTML5(
				$(form?.querySelectorAll(':invalid'))
			);
		}

		if (typeof eventData.validate !== 'undefined') {
			fieldsToUpdateOrCreate['dl.validate'] = eventData.validate;
		}

		if (validator || typeof validator === 'undefined') {
			fieldsToUpdateOrCreate[
				'_custom=SUBMISSION_TIMESTAMP=-1=submissionTimestamp'
			] = eventData.submissionTimestamp;
		}

		fieldsToUpdateOrCreate['dl.formLocation'] = form?.closest?.(
			'.nav-fragment'
		)
			? 'navigation'
			: 'inpage';

		// injectCAIWalletConfig & injectExtraInfoParams are in events/form/submission.js as well
		// just in case the ws-tracking selectors do not find the form for onPage
		if (form?.querySelector('input[name="dl.uuid"]')?.value) {
			injectCAIWalletConfig(win, form);
		}

		injectMyCars(form);
		injectExtraInfoParams(win, form, paramsForExtraInfo, true);
		injectShiftSessionId(form);
		injectUniteCorrelationId(form);

		createOrUpdateHiddenFields(form, fieldsToUpdateOrCreate);

		// Handle PII

		// wipe out existing data
		// then in form confirmation on ajax pages we can tell if this has been written out yet
		// That also indicates that the hashPii function has run
		DDC.dataLayer.confirmation = DDC.dataLayer.confirmation || {};

		const formData = new FormData(form);
		const parameterMap = {};

		formData.forEach((value, key) => {
			parameterMap[key] = value;
		});

		hashPii(parameterMap, win.crypto).then((hashedPii) => {
			const formConfirmation = DDC.dataLayer.confirmation;

			// This is for the EPrice form for same-page submissions
			// Most of the forms lead to a confirmation page otherwise
			({
				fields: {
					firstName: formConfirmation.firstName,
					lastName: formConfirmation.lastName,
					email: formConfirmation.email,
					street: formConfirmation.street
				},
				fieldsTrimmedLowerCase: {
					firstName: formConfirmation.firstNameLowerCase,
					lastName: formConfirmation.lastNameLowerCase,
					email: formConfirmation.emailLowerCase,
					street: formConfirmation.streetLowerCase
				}
			} = hashedPii);
		});

		eventObjects.push({
			eventType,
			eventData
		});

		// US897430: temporary fix, require permanent solution.
		if (eventData.trackingId === 'PROMO-CTA-LEAD') {
			eventObjects.push(await confirmationEvent(win, event));
		}

		eventData.trackingSection = section;
		eventData.mywallet = !!hasMyWalletInteraction(win);
	}

	return eventObjects;
}

export default submission;
